/**
 * AdminHub — Platform Administration workspace chrome (Platform Owner only).
 * Sidebar on desktop, scrollable tabs on mobile. Anyone else gets the bare page.
 */
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Users2, LifeBuoy, ShieldAlert, Bot, ShieldCheck, Building2, CreditCard, Boxes, Inbox, Archive } from "lucide-react";

const SECTIONS = [
  { to: "/admin/platform", icon: ShieldCheck, label: "Overview", exact: true },
  { to: "/admin/platform/organisations", icon: Building2, label: "Organisations" },
  { to: "/admin/platform/subscriptions", icon: CreditCard, label: "Subscriptions" },
  { to: "/admin/platform/enquiries", icon: Inbox, label: "Enquiries" },
  { to: "/admin/platform/retained", icon: Archive, label: "Retained Records" },
  { to: "/admin/platform/modules", icon: Boxes, label: "Modules" },
  { to: "/admin/platform/accounts", icon: Users2, label: "Accounts" },
  { to: "/admin/platform/support", icon: LifeBuoy, label: "Support inbox" },
  { to: "/admin/platform/guardian", icon: Bot, label: "AI Support & Issues" },
  { to: "/admin/platform/security", icon: ShieldAlert, label: "Security" },
];

const AdminHub = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!user?.is_platform_owner) return children;

  const isActive = (s) => (s.exact ? pathname === s.to : pathname.startsWith(s.to));

  const Item = ({ s, compact }) => (
    <Link key={s.to} to={s.to} data-testid={`adminhub-${s.label.toLowerCase().replace(/\s+/g, "-")}${compact ? "-mobile" : ""}`}
      className={`flex items-center gap-2.5 font-bold text-sm transition-colors whitespace-nowrap ${compact ? "px-3 h-10" : "px-3.5 h-11"} ${
        isActive(s) ? "bg-[#1E1B4B] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
      <s.icon size={15} strokeWidth={2.5}/>{s.label}
    </Link>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6" data-testid="admin-hub">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-teal-700 mb-3 flex items-center gap-1.5">
        <ShieldCheck size={11} strokeWidth={3}/> Platform Administration
      </p>
      {/* Mobile: horizontal tabs */}
      <div className="lg:hidden flex gap-1 overflow-x-auto border-2 border-slate-200 bg-white p-1 mb-4" data-testid="admin-hub-tabs">
        {SECTIONS.map((s) => <Item key={s.to} s={s} compact />)}
      </div>
      <div className="lg:grid lg:grid-cols-[13rem_1fr] lg:gap-6 lg:items-start">
        {/* Desktop: sidebar */}
        <aside className="hidden lg:flex flex-col gap-1 border-2 border-slate-200 bg-white p-2 sticky top-20" data-testid="admin-hub-sidebar">
          {SECTIONS.map((s) => <Item key={s.to} s={s} />)}
        </aside>
        <div className="min-w-0 [&>*]:!pt-0">{children}</div>
      </div>
    </div>
  );
};

export default AdminHub;
